import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  Users, Clock, DollarSign, Package, BookOpen, FolderOpen, 
  AlertTriangle, CheckCircle, RefreshCw, Calendar, TrendingUp,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

export default function OperationsDashboard() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/operations/dashboard');
      setDashboard(res.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load operations dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const { staff_today, timesheets_pending, payroll_status, inventory_alerts, training_alerts, onboarding_incomplete } = dashboard || {};

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Operations Dashboard</h1>
          <p className="text-slate-500">Real-time operational overview</p>
        </div>
        <Button onClick={fetchDashboard} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Staff Today</p>
                <p className="text-2xl font-bold text-blue-600">
                  {(staff_today?.employee_count || 0) + (staff_today?.physician_count || 0)}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={timesheets_pending?.count > 0 ? 'border-amber-200 bg-amber-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Pending Timesheets</p>
                <p className={`text-2xl font-bold ${timesheets_pending?.count > 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                  {timesheets_pending?.count || 0}
                </p>
              </div>
              <Clock className={`h-8 w-8 ${timesheets_pending?.count > 0 ? 'text-amber-400' : 'text-slate-400'}`} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Payroll Status</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {payroll_status?.employees_processed || 0}/{(payroll_status?.employees_processed || 0) + (payroll_status?.employees_pending || 0)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={inventory_alerts?.pending_requests > 0 ? 'border-orange-200 bg-orange-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Supply Requests</p>
                <p className={`text-2xl font-bold ${inventory_alerts?.pending_requests > 0 ? 'text-orange-600' : 'text-slate-600'}`}>
                  {inventory_alerts?.pending_requests || 0}
                </p>
              </div>
              <Package className={`h-8 w-8 ${inventory_alerts?.pending_requests > 0 ? 'text-orange-400' : 'text-slate-400'}`} />
            </div>
          </CardContent>
        </Card>

        <Card className={training_alerts?.overdue_count > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Training Overdue</p>
                <p className={`text-2xl font-bold ${training_alerts?.overdue_count > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                  {training_alerts?.overdue_count || 0}
                </p>
              </div>
              <BookOpen className={`h-8 w-8 ${training_alerts?.overdue_count > 0 ? 'text-red-400' : 'text-slate-400'}`} />
            </div>
          </CardContent>
        </Card>

        <Card className={onboarding_incomplete?.incomplete_count > 0 ? 'border-purple-200 bg-purple-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Onboarding</p>
                <p className={`text-2xl font-bold ${onboarding_incomplete?.incomplete_count > 0 ? 'text-purple-600' : 'text-slate-600'}`}>
                  {onboarding_incomplete?.incomplete_count || 0}
                </p>
              </div>
              <FolderOpen className={`h-8 w-8 ${onboarding_incomplete?.incomplete_count > 0 ? 'text-purple-400' : 'text-slate-400'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detail Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Staff Scheduled Today */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                Staff Scheduled Today
              </CardTitle>
              <Link to="/schedules" className="text-sm text-teal-600 hover:underline flex items-center">
                View All <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {staff_today?.employees?.length === 0 && staff_today?.physicians?.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No staff scheduled for today</p>
            ) : (
              <div className="space-y-4">
                {staff_today?.employees?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">EMPLOYEES ({staff_today.employee_count})</p>
                    <div className="space-y-2">
                      {staff_today.employees.slice(0, 5).map((emp, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                          <span className="font-medium">{emp.name}</span>
                          <span className="text-sm text-slate-500">{emp.shift} • {emp.start_time}-{emp.end_time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {staff_today?.physicians?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">PHYSICIANS ({staff_today.physician_count})</p>
                    <div className="space-y-2">
                      {staff_today.physicians.slice(0, 3).map((phy, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-blue-50 rounded">
                          <span className="font-medium">{phy.name}</span>
                          <span className="text-sm text-slate-500">{phy.start_time}-{phy.end_time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Timesheets */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-500" />
                Timesheets Pending Approval
              </CardTitle>
              <Link to="/timesheets" className="text-sm text-teal-600 hover:underline flex items-center">
                Review <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {timesheets_pending?.timesheets?.length === 0 ? (
              <div className="text-center py-4">
                <CheckCircle className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-slate-500">All timesheets approved</p>
              </div>
            ) : (
              <div className="space-y-2">
                {timesheets_pending.timesheets.slice(0, 5).map((ts) => (
                  <div key={ts.id} className="flex justify-between items-center p-2 bg-amber-50 rounded border border-amber-100">
                    <div>
                      <span className="font-medium">{ts.user_name}</span>
                      <p className="text-xs text-slate-500">{ts.total_hours} hours</p>
                    </div>
                    <Badge variant="outline" className="bg-amber-100 text-amber-700">Pending</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payroll Status */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-500" />
                Current Payroll Period
              </CardTitle>
              <Link to="/payroll/calculator" className="text-sm text-teal-600 hover:underline flex items-center">
                Process <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {payroll_status?.current_period ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Period</span>
                  <span className="font-medium">{payroll_status.current_period.name}</span>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Processing Progress</span>
                    <span>{payroll_status.employees_processed}/{payroll_status.employees_processed + payroll_status.employees_pending}</span>
                  </div>
                  <Progress 
                    value={(payroll_status.employees_processed / (payroll_status.employees_processed + payroll_status.employees_pending || 1)) * 100} 
                    className="h-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="text-center p-3 bg-slate-50 rounded">
                    <p className="text-xs text-slate-500">Gross Total</p>
                    <p className="text-lg font-bold text-slate-900">${payroll_status.total_gross?.toLocaleString()}</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded">
                    <p className="text-xs text-slate-500">Net Total</p>
                    <p className="text-lg font-bold text-emerald-600">${payroll_status.total_net?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">No open pay period</p>
            )}
          </CardContent>
        </Card>

        {/* Onboarding Incomplete */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-purple-500" />
                Incomplete Onboarding
              </CardTitle>
              <Link to="/admin/onboarding-status" className="text-sm text-teal-600 hover:underline flex items-center">
                View All <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {onboarding_incomplete?.employees?.length === 0 ? (
              <div className="text-center py-4">
                <CheckCircle className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-slate-500">All employees onboarded</p>
              </div>
            ) : (
              <div className="space-y-2">
                {onboarding_incomplete.employees.slice(0, 5).map((emp) => (
                  <div key={emp.employee_id} className="p-2 bg-purple-50 rounded border border-purple-100">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium">{emp.employee_name}</span>
                      <Badge variant="outline" className="bg-purple-100 text-purple-700">
                        {emp.progress_percent}%
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500">
                      Missing: {emp.missing_documents?.join(', ') || 'None'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alerts Section */}
      {(training_alerts?.overdue_count > 0 || inventory_alerts?.pending_requests > 0) && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Action Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {training_alerts?.overdue_count > 0 && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <h4 className="font-medium text-red-700 mb-2">Training Overdue ({training_alerts.overdue_count})</h4>
                  <ul className="text-sm space-y-1">
                    {training_alerts.overdue_items?.slice(0, 3).map((item, idx) => (
                      <li key={idx} className="text-red-600">
                        {item.employee_name} - {item.training_title}
                      </li>
                    ))}
                  </ul>
                  <Link to="/admin/training-compliance" className="text-sm text-red-700 hover:underline mt-2 block">
                    View all →
                  </Link>
                </div>
              )}
              {inventory_alerts?.pending_requests > 0 && (
                <div className="p-4 bg-orange-50 rounded-lg">
                  <h4 className="font-medium text-orange-700 mb-2">Supply Requests ({inventory_alerts.pending_requests})</h4>
                  <ul className="text-sm space-y-1">
                    {inventory_alerts.pending_items?.slice(0, 3).map((item, idx) => (
                      <li key={idx} className="text-orange-600">
                        {item.item} (x{item.quantity}) - {item.status}
                      </li>
                    ))}
                  </ul>
                  <Link to="/supplies" className="text-sm text-orange-700 hover:underline mt-2 block">
                    Review requests →
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
